// React Error Boundary — 하위 트리 render/lifecycle 예외를 잡아
// 화이트스크린 대신 에러 메시지 + 새로고침 버튼을 보여준다.
import { Component, type ErrorInfo, type ReactNode } from 'react';
import i18n from '../i18n';

interface Props { children: ReactNode }
interface State { hasError: boolean; error: Error | null }

export class ErrorBoundary extends Component<Props, State> {
	state: State = { hasError: false, error: null };

	static getDerivedStateFromError(error: Error): State {
		return { hasError: true, error };
	}

	componentDidCatch(error: Error, info: ErrorInfo): void {
		// 콘솔 + 향후 원격 로깅 후크 포인트
		console.error('[ErrorBoundary]', error, info);
	}

	render() {
		if (!this.state.hasError) return this.props.children;
		const err = this.state.error;
		return (
			// 2026-08-12 사용자: "시스템 오류가 아니라 사용자가 인지할 수 있는 오류가 났으면 함"
			//   → 스택/영문 예외를 전면에 띄우지 않고, 무엇을 하면 되는지 안내. 기술 상세는 접어서 제공(문의용).
			<div className="flex h-[100dvh] flex-col items-center justify-center bg-red-50 p-8 text-center">
				<h2 className="mb-3 text-ui-2xl font-bold text-red-600">{i18n.t('shell.errorOccurred')}</h2>
				<p className="mb-4 max-w-md text-ui-sm text-fg">{i18n.t('shell.errorHint', { defaultValue: '화면을 표시하는 중 문제가 생겼습니다. 새로고침하면 대부분 해결됩니다. 계속되면 관리자에게 알려 주세요.' })}</p>
				<details className="max-w-2xl text-left">
					<summary className="cursor-pointer text-ui-xs text-fg-subtle">{i18n.t('shell.errorDetails', { defaultValue: '기술 정보 보기 (문의 시 첨부)' })}</summary>
					<pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap rounded bg-surface p-4 text-ui-xs text-fg-muted shadow">
						{err?.message}
						{'\n\n'}
						{err?.stack}
					</pre>
				</details>
				<button type="button"
					onClick={() => window.location.reload()}
					className="mt-4 rounded bg-blue-500 px-6 py-2 text-white hover:bg-blue-600">
					{i18n.t('shell.refresh')}
				</button>
			</div>
		);
	}
}

export default ErrorBoundary;
